import cn from 'classnames'
import { StatusButtons } from 'legacy/lut/components/widgets/public/partner360/actionsPlan/keyInitiatives/commonComponents/editorComponents/components/statusButtons/StatusButtons'
import { KeyInitiativeInitTypes } from 'legacy/lut/components/widgets/public/partner360/actionsPlan/keyInitiatives/models/keyInitiativeInitTypes'
import { useAppDispatch } from 'legacy/lut/hooks/useAppDispatch'
import { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'

import { FormTextarea, updateFormDataField, useGetFormData } from 'cng-common/features/form'
import { Translation } from 'cng-common/features/translations/components/translation'
import { selectTranslationMode } from 'cng-common/features/translations/selectors'
import { Grid, Item } from 'core-components/library/grid'
import { LoaderOverlay } from 'core-components/library/loader'

import { ProgressDropdown } from '../../../../../../commonComponents/editorComponents/components/progressDropdown/ProgressDropdown'
import { TRANSLATION_KEYS as tr } from '../../../../../../translations'
import { PROGRESS_VIEW_ITEMS, PROGRESS_VIEW_LABEL_SUFFIX } from '../../../configs'
import { getPracticeDevelopmentProgressValues } from '../../../helpers/getPracticeDevelopmentProgressValues'
import { IKeyInitiativeSectionProps } from '../../../models/IKeyInitiativeSectionProps'

import styles from './StatusSection.module.scss'

export const StatusSection = (props: IKeyInitiativeSectionProps) => {
  const { initialData, isPracticeDevelopment, initiativeFormKey } = props

  /** -------------------- Selectors ----------------------- */

  const translationMode = useSelector(selectTranslationMode)

  /** -------------------- Hooks ----------------------- */

  const dispatch = useAppDispatch()
  const [{ formData }] = useGetFormData(initiativeFormKey, initialData)

  /** -------------------- Variables ----------------------- */

  const isTranslationMode = useMemo(() => translationMode === 'edit', [translationMode])

  const isAutoCreatedKeyInitiative = useMemo(
    () => formData?.initType === KeyInitiativeInitTypes.ImportedInitiative,
    [formData?.initType]
  )

  const progressItems = useMemo(
    () =>
      isPracticeDevelopment
        ? getPracticeDevelopmentProgressValues(formData.currentProgress).progressItems
        : [],
    [formData.currentProgress, isPracticeDevelopment]
  )

  const handleToggleItem = useCallback(
    (key: string) => {
      if (!isPracticeDevelopment) {
        return
      }

      const next = progressItems.includes(key)
        ? progressItems.filter((k) => k !== key)
        : [...progressItems, key]
      const progressValues = getPracticeDevelopmentProgressValues(next.join(','))

      dispatch(
        updateFormDataField({
          field: 'currentProgress',
          value: progressValues.currentProgress,
          formKey: initiativeFormKey,
        })
      )
      dispatch(
        updateFormDataField({
          field: 'followUp',
          value: progressValues.followUp,
          formKey: initiativeFormKey,
        })
      )
    },
    [progressItems, dispatch, initiativeFormKey, isPracticeDevelopment]
  )

  const selectedItems = useMemo(
    () => PROGRESS_VIEW_ITEMS.filter((item) => progressItems.includes(item.key)),
    [progressItems]
  )

  const unselectedItems = useMemo(
    () => PROGRESS_VIEW_ITEMS.filter((item) => !progressItems.includes(item.key)),
    [progressItems]
  )

  /** -------------------- Render ----------------------- */

  return (
    <div className={styles.root}>
      <LoaderOverlay show={false} />

      <Grid noRootMargin direction="row">
        <Item xs={12} className={styles.labelItem}>
          <Translation text={tr.keyInitiativeEditor.progressView.statusLabel} />
        </Item>

        <Item xs={12}>
          <StatusButtons
            formKey={initiativeFormKey}
            isDisabled={isAutoCreatedKeyInitiative}
            initialValue={initialData?.status}
          />
        </Item>
      </Grid>

      {isPracticeDevelopment && (
        <>
          <Grid noRootMargin direction="row" className={styles.fieldsetContainer}>
            <Item xs={12} className={styles.sectionHeading}>
              <Translation text={tr.keyInitiativeEditor.progressView.groupHint} />
            </Item>

            <Item xs={12}>
              <ProgressDropdown progressItems={progressItems} onToggleItem={handleToggleItem} />
              {isTranslationMode && (
                <Translation text={tr.keyInitiativeEditor.progressView.selectorPlaceholder} />
              )}
            </Item>
          </Grid>

          <Grid noRootMargin direction="row" className={styles.fieldsetContainer}>
            <Item xs={6} className={cn(styles.labelItem, styles.progressLabel)}>
              <Translation
                text={tr.keyInitiativeEditor.businessUnitAndFocusFieldGroup.currentProgress}
              />
            </Item>

            <Item xs={6} className={cn(styles.labelItem, styles.progressLabel)}>
              <Translation text={tr.keyInitiativeEditor.businessUnitAndFocusFieldGroup.followUp} />
            </Item>

            <Item xs={6}>
              <ul className={styles.progressList}>
                {selectedItems.map((item) => (
                  <li key={item.key}>
                    <Translation text={`${item.key}${PROGRESS_VIEW_LABEL_SUFFIX}`} />
                  </li>
                ))}
              </ul>
            </Item>

            <Item xs={6}>
              <ul className={styles.progressList}>
                {unselectedItems.map((item) => (
                  <li key={item.key}>
                    <Translation text={`${item.key}${PROGRESS_VIEW_LABEL_SUFFIX}`} />
                  </li>
                ))}
              </ul>
            </Item>
          </Grid>
        </>
      )}

      {!isPracticeDevelopment && (
        <>
          <Grid noRootMargin direction="row" className={styles.fieldsetContainer}>
            <Item xs={12} className={styles.labelItem}>
              <div>
                <Translation text={tr.keyInitiativeEditor.progressView.currentProgressLabel} />
              </div>
            </Item>
            <Item xs={12}>
              <FormTextarea
                defaultValue={initialData?.currentProgress || ''}
                field="currentProgress"
                formKey={initiativeFormKey}
                placeholder={tr.keyInitiativeEditor.progressView.currentProgressPlaceholder}
                rows={5}
              />
            </Item>
            {isTranslationMode && (
              <Item xs={12}>
                <Translation
                  text={tr.keyInitiativeEditor.progressView.currentProgressPlaceholder}
                />
              </Item>
            )}
          </Grid>

          <Grid noRootMargin direction="row" className={styles.fieldsetContainer}>
            <Item xs={12} className={styles.labelItem}>
              <div>
                <Translation text={tr.keyInitiativeEditor.progressView.followUpLabel} />
              </div>
            </Item>
            <Item xs={12}>
              <FormTextarea
                defaultValue={initialData?.followUp || ''}
                field="followUp"
                formKey={initiativeFormKey}
                placeholder={tr.keyInitiativeEditor.progressView.followUpPlaceholder}
                rows={5}
              />
            </Item>
            {isTranslationMode && (
              <Item xs={12}>
                <Translation text={tr.keyInitiativeEditor.progressView.followUpPlaceholder} />
              </Item>
            )}
          </Grid>
        </>
      )}
    </div>
  )
}
